import { ReactNode, useEffect, useState, lazy } from 'react';
import { CircularProgress, CssBaseline } from '@mui/material';
import { isMaintenanceMode, isMaintenanceAllowedIPs } from 'src/appConfig';
import ThemeProvider from 'src/theme/ThemeProvider';
import StatusCheck from 'src/content/pages/Status/StatusCheck';

const StatusMaintenance = lazy(
  () => import('src/content/pages/Status/StatusMaintenance')
);

interface MaintenanceGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Checks if the user's IP is allowed during maintenance mode
 * by fetching their current IP and comparing it against the allowed IPs whitelist.
 */
const authenticateIPAccess = async (): Promise<boolean> => {
  if (!isMaintenanceMode) {
    return true; // Maintenance mode is inactive, access is allowed
  }

  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (!response.ok) {
      throw new Error('Failed to fetch IP address');
    }
    const data = await response.json();
    const ipAddress = data?.ip;

    if (isMaintenanceAllowedIPs?.includes(ipAddress)) {
      return true; // IP is whitelisted
    }
    return false; // IP is not in allowed list
  } catch (error) {
    console.error('Error verifying maintenance IP access:', error);
    return false;
  }
};

/**
 * `MaintenanceGuard` checks if the application is in maintenance mode.
 * If maintenance mode is enabled and the user's IP is not whitelisted,
 * it renders the maintenance status page. Otherwise, it renders children.
 */
export default function MaintenanceGuard({
  children,
  fallback
}: MaintenanceGuardProps): JSX.Element | null {
  const [isChecking, setIsChecking] = useState<boolean>(isMaintenanceMode);
  const [isAllowed, setIsAllowed] = useState<boolean>(!isMaintenanceMode);

  useEffect(() => {
    let isMounted = true;

    const checkMaintenance = async () => {
      
      if (!isMaintenanceMode) {
        if (isMounted) {
          setIsAllowed(true);
          setIsChecking(false);
        }
        return;
      }

      setIsChecking(true);
      const allowed = await authenticateIPAccess();
      if (isMounted) {
        setIsAllowed(allowed);
        setIsChecking(false);
      }
    };

    checkMaintenance();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isChecking) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress size={50} sx={{ color: 'var(--color-primary)' }} />
      </div>
    );
  }

  if (!isAllowed) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <ThemeProvider>
        <CssBaseline />
        <StatusMaintenance />
      </ThemeProvider>
    );
  }

  return <>{children}</>;
}
